import $ from 'cash-dom';

class StorySlider {
	constructor($container) {
		this._$container = $container;
		this._$slides = $container.find('.story-slider-slide');
		this._$progressSegments = $container.find('.story-slider-progress-segment');
		this._$cta = $container.find('.story-slider-cta');
		this._currentSlide = 0;
		this._totalSlides = this._$slides.length;
		this._isPaused = false;
		this._progressDuration = 3000;
		this._remainingTime = this._progressDuration;
		this._slideStartTime = 0;
		this._autoAdvanceTimer = null;
		this._holdTimeout = null;
		this._pressStartTime = 0;
	}

	init() {
		this._showSlide(0);
		this._bindEvents();
		this._startAutoAdvance();
	}

	_bindEvents() {
		const $nav = this._$container.find('.story-slider-nav');
		const HOLD_DELAY = 140;

		$nav.on('pointerdown', (e) => {
			if ($(e.target).closest('.story-slider-cta').length) {
				return;
			}

			this._pressStartTime = Date.now();

			// If we are near the end, pause immediately to avoid missing the hold
			if (this._remainingTime <= HOLD_DELAY) {
				this._pause();
				return;
			}

			// Wait before pausing - if released before that, it's a tap
			this._holdTimeout = setTimeout(() => this._pause(), HOLD_DELAY);
		});

		const endPress = (e) => {
			this._clearHoldTimeout();

			// If we actually paused, resume now
			if (this._isPaused) {
				this._resume();
				return;
			}

			const pressDuration = Date.now() - this._pressStartTime;
			if (pressDuration < HOLD_DELAY) {
				const $target = $(e.target);
				if ($target.closest('.story-slider-nav-next').length) {
					this._goToNextSlide();
				} else if ($target.closest('.story-slider-nav-prev').length) {
					this._goToPreviousSlide();
				} else {
					// Fallback: decide by tap position
					const navRect = $nav[0].getBoundingClientRect();
					const tapX = e.clientX || (e.changedTouches && e.changedTouches[0] && e.changedTouches[0].clientX);
					if (tapX !== undefined && tapX >= navRect.left + navRect.width / 2) {
						this._goToNextSlide();
					} else {
						this._goToPreviousSlide();
					}
				}
			}
		};

		$(window).on('pointerup', endPress);
		$(window).on('pointercancel', endPress);
	}

	_showSlide(index) {
		this._clearHoldTimeout();
		this._pauseAllVideos();

		// Update slides
		this._$slides.removeClass('active');
		this._$slides.eq(index).addClass('active');

		// Update progress segments
		this._$progressSegments.removeClass('active completed');

		for (let i = 0; i < index; i++) {
			this._$progressSegments.eq(i).addClass('completed');
		}

		// Force restart animation by removing and re-adding the 'active' class
		const $currentSegment = this._$progressSegments.eq(index);
		$currentSegment[0].offsetHeight;
		$currentSegment.addClass('active');

		if (index === this._totalSlides - 1) {
			this._$cta.addClass('visible');
		} else {
			this._$cta.removeClass('visible');
		}

		this._playCurrentSlideVideo(true);
	}

	_pauseAllVideos() {
		this._$slides.find('video').each(function() {
			this.pause();
		});
	}

	_playCurrentSlideVideo(resetTime = false) {
		const $currentSlide = this._$slides.eq(this._currentSlide);
		const $video = $currentSlide.find('video');

		if ($video.length) {
			const video = $video[0];

			// Only reset time when switching slides, not when resuming from pause
			if (resetTime) {
				video.currentTime = 0;
			}

			// Catch play() promise to avoid unhandled rejection
			const playPromise = video.play();
			if (playPromise !== undefined) {
				playPromise.catch(() => {
					// Autoplay was prevented or interrupted - this is OK, ignore silently
				});
			}
		}
	}

	_pauseCurrentSlideVideo() {
		const $currentSlide = this._$slides.eq(this._currentSlide);
		const $video = $currentSlide.find('video');

		if ($video.length) {
			$video[0].pause();
		}
	}

	_startAutoAdvance() {
		this._startTimer(this._remainingTime);
	}

	_stopAutoAdvance() {
		if (this._autoAdvanceTimer) {
			clearTimeout(this._autoAdvanceTimer);
			this._autoAdvanceTimer = null;
		}
	}

	_pause() {
		if (this._isPaused) {
			return;
		}

		this._remainingTime = this._getRemainingTime();

		this._isPaused = true;
		this._$container.addClass('paused');
		this._stopAutoAdvance();
		this._pauseCurrentSlideVideo();
	}

	_resume() {
		if (!this._isPaused) {
			return;
		}

		this._isPaused = false;
		this._$container.removeClass('paused');
		this._playCurrentSlideVideo();
		this._startAutoAdvance();
	}

	_goToNextSlide() {
		this._currentSlide++;

		if (this._currentSlide >= this._totalSlides) {
			this._currentSlide = 0;
		}

		this._resetTimer();
		this._showSlide(this._currentSlide);
		this._startAutoAdvance();
	}

	_goToPreviousSlide() {
		this._currentSlide--;

		if (this._currentSlide < 0) {
			this._currentSlide = this._totalSlides - 1;
		}

		this._resetTimer();
		this._showSlide(this._currentSlide);
		this._startAutoAdvance();
	}

	_startTimer(duration) {
		this._stopAutoAdvance();
		this._slideStartTime = Date.now();
		this._autoAdvanceTimer = setTimeout(() => this._goToNextSlide(), duration);
	}

	_resetTimer() {
		this._remainingTime = this._progressDuration;
	}

	_getRemainingTime() {
		const elapsed = Date.now() - this._slideStartTime;
		return Math.max(0, this._remainingTime - elapsed);
	}

	_clearHoldTimeout() {
		if (this._holdTimeout) {
			clearTimeout(this._holdTimeout);
			this._holdTimeout = null;
		}
	}
}

// Initialize all story sliders on the page
StorySlider.initAll = function() {
	$('.story-slider').each(function() {
		const instance = new StorySlider($(this));
		instance.init();
	});
};

export default StorySlider;
